import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from 'app/javascript/components/ui/avatar';
import { cn } from 'app/javascript/lib/utils';

interface ProfileAvatarProps {
  photoUrl: string | null;
  name: string;
  rank: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}

// Tailwind size classes per size token. Smaller than the previous design —
// the big glowing 192px hero avatar was overwhelming next to the tighter
// shadcn cards we use elsewhere.
const SIZE_MAP: Record<NonNullable<ProfileAvatarProps['size']>, string> = {
  xs: 'size-8',
  sm: 'size-20',
  md: 'size-28 md:size-32',
  lg: 'size-40',
};

const FALLBACK_TEXT_SIZE: Record<NonNullable<ProfileAvatarProps['size']>, string> = {
  xs: 'text-[11px]',
  sm: 'text-2xl',
  md: 'text-4xl md:text-5xl',
  lg: 'text-6xl',
};

function getInitials(name: string): string {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  return parts[0].substring(0, 2).toUpperCase();
}

const ProfileAvatar: React.FC<ProfileAvatarProps> = ({
  photoUrl,
  name,
  rank: _rank,
  size = 'md',
  className = '',
}) => {
  return (
    <Avatar
      className={cn(
        SIZE_MAP[size],
        'shrink-0 rounded-full bg-secondary',
        // Neutral edge — most avatars (real photos or illustrated personas)
        // already carry their own ring of colour. Belt rank is signalled
        // separately via BeltImage so the avatar should not duplicate it.
        className
      )}
    >
      {photoUrl ? <AvatarImage src={photoUrl} alt={name} className="object-cover" /> : null}
      <AvatarFallback
        className={cn(
          'bg-secondary font-bebas tracking-wide text-muted-foreground select-none',
          FALLBACK_TEXT_SIZE[size]
        )}
      >
        {getInitials(name)}
      </AvatarFallback>
    </Avatar>
  );
};

export default ProfileAvatar;
